import * as PIXI from "pixi.js";
import { DEFAULT_ICON_SIDE_LENGTH, loadIcon } from "../../utils/iconLoader";
import { hexToNumber } from "../../utils/color";
import type { IconProps } from "./types";
import { applyYogaStyle } from "../layoutEngine/applyYogaStyles";
import { coreComponentCommon } from "./coreComponentsCommon";
import type { NodeStyle } from "../layoutEngine/layout.types";
import { defaultNodeLayoutStyle } from "../layoutEngine/layoutConstants";

export type Icon = ReturnType<typeof createIcon>;

export const createIcon = (initialProps: IconProps) => {
  const pixiComponent = new PIXI.Sprite();
  pixiComponent.position.set(-Infinity, -Infinity);

  let wasVisible: boolean = true;
  let props: IconProps & { style: NodeStyle } = structuredClone(defaultProps);
  pixiComponent.width = DEFAULT_ICON_SIDE_LENGTH;
  pixiComponent.height = DEFAULT_ICON_SIDE_LENGTH;

  const {
    yogaNode,
    setLayoutValues,
    getLayoutValues,
    getZIndex,
    isCulled,
    setCulled
  } = coreComponentCommon();
  updateProps(initialProps);

  return {
    get props() {
      return props;
    },
    get pixiComponent() {
      return pixiComponent;
    },
    get yogaNode() {
      return yogaNode;
    },
    isCulled,
    setCulled,
    getLayoutValues,
    setLayoutValues,
    getZIndex,
    updateProps,
    destroy,
    render
  };

  /**
   * Performs some basic comparison between new and old props and
   * triggers appropriate actions based on certain props' changes.
   * For Box component, these include:
   * 1. Updating icon
   * 2. Updating icon's properties like size, color
   * 3. Toggling visibility
   *
   * @param newProps
   */
  function updateProps(newProps: IconProps) {
    // Handle props update
    if (props.icon !== newProps.icon && newProps.icon) {
      // Update icon
      pixiComponent.texture = loadIcon(newProps.icon);
    }

    if (newProps.color) {
      // Update color
      const colorInt = hexToNumber(newProps.color);
      if (colorInt !== pixiComponent.tint) {
        pixiComponent.tint = colorInt;
      }
    }

    if (props.size !== newProps.size && newProps.size) {
      // Update size
      pixiComponent.width = newProps.size;
      pixiComponent.height = newProps.size;
    }

    props = {
      ...defaultProps,
      ...newProps,
      style: {
        ...defaultProps.style,
        ...newProps.style,
        width:
          newProps.size !== undefined
            ? newProps.size
            : DEFAULT_ICON_SIDE_LENGTH,
        height:
          newProps.size !== undefined ? newProps.size : DEFAULT_ICON_SIDE_LENGTH
      }
    };

    applyYogaStyle({ style: props.style, node: yogaNode });
  }

  function render() {
    const { left, top, width } = getLayoutValues();
    const zIndex = getZIndex();
    const isVisible = props.style.visible;

    const shouldBeVisible: boolean = !!isVisible && !isCulled() && width > 0;

    if (shouldBeVisible !== wasVisible) {
      pixiComponent.visible = shouldBeVisible;
      wasVisible = shouldBeVisible;
    }

    pixiComponent.position.x = left;
    pixiComponent.position.y = top;

    pixiComponent.zIndex = zIndex;
  }

  function destroy() {
    yogaNode.free();
    pixiComponent.destroy();
  }
};

const defaultProps: IconProps & { style: NodeStyle } = {
  key: "",
  icon: "",
  size: DEFAULT_ICON_SIDE_LENGTH,
  color: "#000000",
  style: { ...defaultNodeLayoutStyle, flexShrink: 0 }
};
